import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Card,
    CardContent,
    CircularProgress,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    Error as ErrorIcon,
    CreditCard as CreditCardIcon,
    Refresh as RefreshIcon,
    Support as SupportIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    AccountBalance as AccountBalanceIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import subscriptionApi from '../services/subscriptionApi';
import * as Sentry from "@sentry/react";

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount / 100);
};

const getDisplayPlanName = (planName) => {
    const name = planName?.toLowerCase() || '';
    if (name.includes('basic')) return 'Basic';
    if (name.includes('pro') && !name.includes('test')) return 'Growth';
    if (name.includes('enterprise')) return 'Pro';
    return planName;
};

// Common payment error types and their solutions
const ERROR_SOLUTIONS = {
    'insufficient_funds': {
        title: 'Insufficient Funds',
        description: 'Your payment method doesn\'t have enough funds to complete this transaction.',
        icon: <AccountBalanceIcon color="error" />,
        solutions: [
            'Add funds to your account or use a different payment method',
            'Try again after your next payment clears',
            'Contact your bank to ensure the transaction isn\'t blocked'
        ]
    },
    'card_declined': {
        title: 'Card Declined',
        description: 'Your payment method was declined by your bank or card issuer.',
        icon: <CreditCardIcon color="error" />,
        solutions: [
            'Verify your card details are correct',
            'Check if your card has expired',
            'Contact your bank to authorize the transaction',
            'Try using a different payment method'
        ]
    },
    'authentication_required': {
        title: 'Authentication Required',
        description: 'Your bank requires additional authentication for this transaction.',
        icon: <SecurityIcon color="error" />,
        solutions: [
            'Complete 3D Secure authentication if prompted',
            'Check for SMS or app notifications from your bank',
            'Contact your bank if you\'re not receiving authentication prompts'
        ]
    },
    'generic': {
        title: 'Payment Processing Error',
        description: 'We encountered an issue processing your payment.',
        icon: <ErrorIcon color="error" />,
        solutions: [
            'Try the payment again in a few minutes',
            'Verify your payment method details',
            'Contact our support team if the issue persists'
        ]
    }
};

const getErrorInfo = (errorCode) => {
    return ERROR_SOLUTIONS[errorCode] || ERROR_SOLUTIONS.generic;
};

const PaymentFailedModal = ({ 
    open, 
    onClose, 
    planChangeRequest, 
    onRetry,
    onUpdatePaymentMethod 
}) => {
    const [retryLoading, setRetryLoading] = useState(false);
    const [billingPortalLoading, setBillingPortalLoading] = useState(false);

    const handleRetryPayment = async () => {
        if (!planChangeRequest?.id || !onRetry) return;

        try {
            setRetryLoading(true);
            await subscriptionApi.retryPlanChangePayment(planChangeRequest.id);
            if (onRetry) onRetry();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error retrying payment:', error);
            // The parent component should handle the error display
        } finally {
            setRetryLoading(false);
        }
    };

    const handleUpdatePaymentMethod = async () => {
        try {
            setBillingPortalLoading(true);
            const portalResponse = await subscriptionApi.getBillingPortalUrl();
            
            // Open billing portal in new tab
            window.open(portalResponse.url, '_blank');
            
            if (onUpdatePaymentMethod) onUpdatePaymentMethod();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error opening billing portal:', error);
        } finally {
            setBillingPortalLoading(false);
        }
    };

    const errorInfo = getErrorInfo(planChangeRequest?.failure_reason);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Payment Failed
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {/* Plan Change Summary */}
                {planChangeRequest && (
                    <Card sx={{ mb: 3, background: 'rgba(0,0,0,0.02)' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Failed Plan Change
                            </Typography>
                            
                            <List dense sx={{ p: 0 }}>
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemText
                                        primary="Target Plan"
                                        secondary={getDisplayPlanName(planChangeRequest.new_plan_name)}
                                    />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(planChangeRequest.new_plan_price_cents)}/month
                                    </Typography>
                                </ListItem>
                                
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemText
                                        primary="Attempted Charge"
                                        secondary="Amount we tried to charge"
                                    />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                                        {formatCurrency(planChangeRequest.immediate_charge_cents)}
                                    </Typography>
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                )}

                {/* Error Information */}
                <Alert 
                    severity="error" 
                    sx={{ mb: 3 }}
                    icon={errorInfo.icon}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {errorInfo.title}
                    </Typography>
                    <Typography variant="body2">
                        {errorInfo.description}
                    </Typography>
                </Alert>

                {/* Solutions */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon color="primary" />
                        What You Can Do
                    </Typography>
                    
                    <List dense>
                        {errorInfo.solutions.map((solution, index) => (
                            <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                                <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                    <Box 
                                        sx={{ 
                                            width: 20, 
                                            height: 20, 
                                            borderRadius: '50%', 
                                            background: 'primary.main', 
                                            color: 'white', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            fontSize: 12,
                                            fontWeight: 600
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                </ListItemIcon>
                                <ListItemText
                                    primary={solution}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        sx: { fontWeight: 500 }
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Additional Help */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Need More Help?
                    </Typography>
                    <Typography variant="body2">
                        If you continue experiencing issues, our support team is here to help. 
                        We can assist with payment troubleshooting and alternative payment options.
                    </Typography>
                </Alert>

                {/* No Charges Information */}
                <Alert severity="success">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                        No charges were applied to your account. Your current plan remains unchanged.
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button
                        onClick={onClose}
                        sx={{ borderRadius: 2, order: { xs: 3, sm: 1 } }}
                        size="large"
                    >
                        Close
                    </Button>
                    
                    <Button
                        onClick={handleUpdatePaymentMethod}
                        variant="outlined"
                        disabled={billingPortalLoading}
                        startIcon={billingPortalLoading ? <CircularProgress size={20} /> : <CreditCardIcon />}
                        sx={{ 
                            borderRadius: 2,
                            order: { xs: 2, sm: 2 },
                            minWidth: { xs: 'auto', sm: 180 }
                        }}
                        size="large"
                    >
                        {billingPortalLoading ? 'Opening...' : 'Update Payment Method'}
                    </Button>
                    
                    <Button
                        onClick={handleRetryPayment}
                        variant="contained"
                        disabled={retryLoading}
                        startIcon={retryLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                            order: { xs: 1, sm: 3 },
                            minWidth: { xs: 'auto', sm: 140 }
                        }}
                        size="large"
                    >
                        {retryLoading ? 'Retrying...' : 'Retry Payment'}
                    </Button>
                </Box>
            </DialogActions>

            {/* Support Contact Information */}
            <Box sx={{ 
                p: 2, 
                background: 'rgba(0,0,0,0.02)', 
                borderTop: '1px solid rgba(0,0,0,0.1)',
                borderRadius: '0 0 12px 12px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <SupportIcon color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary">
                        Still having trouble? Contact support at support@horizondatawave.com
                    </Typography>
                </Box>
            </Box>
        </Dialog>
    );
};

export default PaymentFailedModal; 